// @flow
import { useContext, useState, useEffect, useRef } from 'react';
import { createRequest, Context } from 'urql';
import type { TypedDocumentNode, UseQueryState } from 'urql';
import { pipe, subscribe } from 'wonka';
import { useInvalidatableQuery, executeInvalidatableQuery } from './denormalizedCacheExchange';
import type { InvalidationPredicateResponse } from './denormalizedCacheExchange';

type UsePaginatedQueryArgs<T, V: {}> = {|
  query: string | TypedDocumentNode<T, V>,
  variables?: V,
  combineData: (currentData: T, nextData: ?T, variables: V) => T,
  showFetchingAfterFirstQuery?: boolean,
  invalidationPredicates?: {
    [string]: (variables: V, value: any) => InvalidationPredicateResponse,
  },
  executeWhen?: 'immediately' | 'afterInvalidation' | 'never',
|};

type UsePaginatedQueryResponse<T, V: {}> = [
  /**
   * @see useQuery
   */
  UseQueryState<T>,

  /**
   * Fetch the next page of data.
   *
   * @param nextVariables - variables to change, such as an increased offset or page number
   * @return a promise that resolves when the next query has finished
   */
  (nextVariables: $Shape<V>) => Promise<void>,
];

/**
 * A helper function that either updates slice of <code>target</code> array starting with <code>offset</code> with <code>source</code> data,
 * or, if <code>target</code> is too small, expand it and append <code>source</code> to the end.
 */
export const embedArray = <T>(
  target: ?$ReadOnlyArray<T>,
  source: $ReadOnlyArray<T>,
  offset: number,
  limit: number,
): $ReadOnlyArray<T> => {
  let result;
  if (!target) {
    result = [...source];
  } else {
    if (target.length < offset) {
      result = [...target];
    } else {
      result = target.slice(0, offset);
    }
    result = result.concat(source);
    if (result.length < target.length) {
      // Expand and append items to the end only source is not empty and it's not the last page.
      // When an item from the last page was deleted, we don't need to add it back from target
      if (target.slice(offset + limit).length && source.length) {
        result = result.concat(target.slice(result.length));
      }
    }
  }
  return result;
};

/**
 * A clone of {@link usePaginatedQuery} that better handles changes to the paged list. Unlike {@link usePaginatedQuery}, this implementation keeps the loaded vector and implies
 * that <code>combineData</code> would update is own part with the new data. This solves the problem that invalidated callbacks order is not defined. This implementation also
 * maintains the internal state as a reference, making sure that all invalidated callbacks are working with up to date data.
 *
 * @param query - a GraphQL query (see {@link useQuery})
 * @param variables - the initial variables to fetch the first page
 * @param combineData - a function to merge a page of data onto the previously-fetched data
 * @param showFetchingAfterFirstQuery - If true, all new queries will set the "fetching" state to true. Default is for only the first query to set fetching to true.
 * @param invalidationPredicates - A map of mutation field name to a predicate on the mutation's variables, which will invalidate this query. See useInvalidatableQuery for more
 * @param skipQuery - do not execute the query (@see {@link useQuery})
 */
const useMutablePaginatedQuery = <T, V: {}>({
  query,
  variables,
  combineData,
  showFetchingAfterFirstQuery,
  invalidationPredicates,
  executeWhen,
}: UsePaginatedQueryArgs<T, V>): UsePaginatedQueryResponse<T, V> => {
  const client = useContext(Context);

  const boundPredicates = {};
  if (invalidationPredicates) {
    Object.entries(invalidationPredicates).forEach(([name, func]) => {
      // $FlowFixMe func is a function
      boundPredicates[name] = func.bind(null, variables);
    });
  }

  // eslint-disable-next-line ta-code-conventions/use-invalidatable-query-literal-predicates
  const [initialState] = useInvalidatableQuery({ query, variables, executeWhen }, boundPredicates);
  const [currentVariables, setCurrentVariables] = useState<V | void>(variables);

  const stateRef = useRef(initialState);
  const [currentState, setCurrentState] = useState(initialState);

  useEffect(() => {
    setCurrentVariables(variables);
    if (stateRef.current && stateRef.current.data && variables && !initialState.fetching) {
      const { data } = stateRef.current;
      // if we are invalidating current state, merge updated data with existing
      stateRef.current = {
        ...initialState,
        data: combineData(data, initialState.data, variables),
      };
    } else {
      stateRef.current = initialState;
    }
    setCurrentState(stateRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState.data, initialState.fetching, createRequest(query, variables).key]);

  return [
    currentState,
    nextVariables => {
      const newCurrentVariables = { ...currentVariables, ...nextVariables };
      setCurrentVariables(newCurrentVariables);
      if (showFetchingAfterFirstQuery) {
        stateRef.current = { ...stateRef.current, fetching: true };
        setCurrentState(stateRef.current);
      }

      const newBoundPredicates = {};
      Object.entries(invalidationPredicates || {}).forEach(([name, func]) => {
        // $FlowFixMe func is a function
        newBoundPredicates[name] = func.bind(null, newCurrentVariables);
      });

      return new Promise(resolve =>
        pipe(
          executeInvalidatableQuery(
            client,
            { query, variables: newCurrentVariables },
            newBoundPredicates,
          ),
          subscribe(nextState => {
            if (stateRef.current.data != null) {
              const data = combineData(stateRef.current.data, nextState.data, newCurrentVariables);
              stateRef.current = {
                ...nextState,
                data,
              };
            } else {
              stateRef.current = nextState;
            }
            setCurrentState(stateRef.current);
            resolve();
          }),
        ),
      );
    },
  ];
};

export default useMutablePaginatedQuery;
